// EVENTSFLO-CLIENT-CHANGE-REQUESTS-ADMIN-APPLY-WORKFLOW-V1
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  AlertTriangle,
  Inbox,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const STATUS_TABS = [
  { value: "all", label: "All" },
  { value: "submitted", label: "Submitted" },
  { value: "under_review", label: "Under review" },
  { value: "applied", label: "Applied" },
  { value: "rejected", label: "Rejected" },
];

const TYPE_LABELS = {
  project_change: "Project change",
  project_cancellation: "Project cancellation",
  shift_change: "Shift change",
  shift_cancellation: "Shift cancellation",
};

const STATUS_STYLES = {
  submitted: "bg-amber-500/10 text-amber-200 border-amber-500/40",
  under_review: "bg-blue-500/10 text-blue-200 border-blue-500/40",
  applied: "bg-green-500/10 text-green-200 border-green-500/40",
  rejected: "bg-red-500/10 text-red-200 border-red-500/40",
  cancelled_by_client: "bg-zinc-500/10 text-zinc-200 border-zinc-500/40",
  accepted: "bg-emerald-500/10 text-emerald-200 border-emerald-500/40",
};

const TERMINAL = new Set(["applied", "rejected", "cancelled_by_client"]);

// EVENTSFLO-ADMIN-CHANGE-REQUESTS-DIFF-PREVIEW-V1
// Display labels + ordering for the current-vs-requested diff table.
const FIELD_LABELS = {
  date: "Date",
  start_time: "Start time",
  end_time: "End time",
  duration_hours: "Duration (h)",
  staff_required: "Crew required",
  venue_id: "Venue (id)",
  venue_name: "Venue",
  venue_location_id: "Location (id)",
  venue_location_name: "Location / meeting point",
  activity: "Activity / task",
  name: "Activity / task",
  role: "Crew role / task",
  notes: "Notes",
  project_name: "Project name",
  start_date: "Start date",
  end_date: "End date",
  status: "Status",
  // EVENTSFLO-CLIENT-PORTAL-SHIFT-REQUIREMENTS-LINE-ITEMS-V1
  requirements: "Requested requirements",
};

// EVENTSFLO-CLIENT-PORTAL-SHIFT-REQUIREMENTS-LINE-ITEMS-V1
// Compute added / removed / changed rows between two requirements lists.
// Matching key: `client_catalog_key` if both present, else lowercased
// label. Quantity / unit / category / notes diff per matched pair.
function diffRequirements(before, after) {
  const safe = (v) => (Array.isArray(v) ? v : []);
  const bRows = safe(before).filter((r) => r && (r.label || "").trim());
  const aRows = safe(after).filter((r) => r && (r.label || "").trim());
  const keyOf = (r) =>
    (r.client_catalog_key || "").trim() ||
    (r.label || "").trim().toLowerCase();
  const bMap = new Map();
  const aMap = new Map();
  for (const r of bRows) bMap.set(keyOf(r), r);
  for (const r of aRows) aMap.set(keyOf(r), r);
  const added = [];
  const removed = [];
  const changed = [];
  const unchanged = [];
  for (const [k, r] of aMap) {
    if (!bMap.has(k)) {
      added.push(r);
    } else {
      const prev = bMap.get(k);
      const num = (v) => Number(v ?? 0);
      const diffFields = [];
      if (num(prev.quantity) !== num(r.quantity)) {
        diffFields.push({ key: "quantity", before: num(prev.quantity), after: num(r.quantity) });
      }
      if ((prev.unit || "") !== (r.unit || "")) {
        diffFields.push({ key: "unit", before: prev.unit || "", after: r.unit || "" });
      }
      if ((prev.category || "") !== (r.category || "")) {
        diffFields.push({ key: "category", before: prev.category || "", after: r.category || "" });
      }
      if ((prev.notes || "") !== (r.notes || "")) {
        diffFields.push({ key: "notes", before: prev.notes || "", after: r.notes || "" });
      }
      if ((prev.label || "") !== (r.label || "")) {
        diffFields.push({ key: "label", before: prev.label || "", after: r.label || "" });
      }
      if (diffFields.length > 0) {
        changed.push({ before: prev, after: r, fields: diffFields });
      } else {
        unchanged.push(r);
      }
    }
  }
  for (const [k, r] of bMap) {
    if (!aMap.has(k)) removed.push(r);
  }
  const hasAnyChange = added.length + removed.length + changed.length > 0;
  return { added, removed, changed, unchanged, hasAnyChange };
}

function RequirementLine({ value }) {
  const r = value || {};
  return (
    <span className="inline-flex items-center gap-1.5">
      <strong className="font-semibold">{Number(r.quantity || 0)} ×</strong>
      <span>{r.label || "—"}</span>
      {r.unit && <span className="text-muted-foreground">({r.unit})</span>}
    </span>
  );
}

function RequirementsDiffPanel({ before, after }) {
  const d = diffRequirements(before, after);
  const bRows = Array.isArray(before) ? before.filter((r) => r && r.label) : [];
  const aRows = Array.isArray(after) ? after.filter((r) => r && r.label) : [];
  return (
    <div
      className="rounded border border-amber-500/40 bg-amber-500/5 p-2 space-y-2"
      data-testid="admin-change-request-requirements-diff"
    >
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">
          {FIELD_LABELS.requirements}
        </Label>
        <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
          {d.added.length} added · {d.removed.length} removed · {d.changed.length} changed
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Current ({bRows.length})
          </div>
          {bRows.length === 0 ? (
            <div className="italic text-muted-foreground">— none —</div>
          ) : (
            <ul className="space-y-0.5">
              {bRows.map((r, i) => (
                <li key={`b-${i}`}><RequirementLine value={r} /></li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Requested ({aRows.length})
          </div>
          {aRows.length === 0 ? (
            <div className="italic text-muted-foreground">— none —</div>
          ) : (
            <ul className="space-y-0.5">
              {aRows.map((r, i) => (
                <li key={`a-${i}`} className="text-cyan-200">
                  <RequirementLine value={r} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {d.hasAnyChange && (
        <div className="rounded border border-border/40 bg-background/30 p-2 text-xs space-y-1">
          {d.added.map((r, i) => (
            <div
              key={`add-${i}`}
              className="text-emerald-300"
              data-testid={`admin-change-request-req-added-${i}`}
            >
              + Added <RequirementLine value={r} />
            </div>
          ))}
          {d.removed.map((r, i) => (
            <div
              key={`rem-${i}`}
              className="text-rose-300"
              data-testid={`admin-change-request-req-removed-${i}`}
            >
              − Removed <RequirementLine value={r} />
            </div>
          ))}
          {d.changed.map((c, i) => (
            <div
              key={`chg-${i}`}
              className="text-amber-200"
              data-testid={`admin-change-request-req-changed-${i}`}
            >
              ≠ Changed <RequirementLine value={c.before} /> →{" "}
              <span className="font-medium">
                <RequirementLine value={c.after} />
              </span>
              <span className="text-muted-foreground ml-1">
                ({c.fields.map((f) => f.key).join(", ")})
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// EVENTSFLO-CLIENT-REQUIREMENTS-MAP-TO-TASK-DEFINITIONS-SAFE-V1
// Admin-only mapping controls per requirement row. Renders for the
// REQUESTED list (since that's the version that will be persisted on
// the live shift after Apply). Tenant-scoped Task Definitions are
// fetched once when the dialog opens.
const MAPPING_STATUSES = [
  { value: "unmapped", label: "Unmapped" },
  { value: "mapped", label: "Mapped" },
  { value: "needs_review", label: "Needs review" },
  { value: "ignored", label: "Ignored" },
];

const MAPPING_STATUS_STYLES = {
  unmapped: "bg-zinc-500/10 text-zinc-200 border-zinc-500/30",
  mapped: "bg-emerald-500/10 text-emerald-200 border-emerald-500/30",
  needs_review: "bg-amber-500/10 text-amber-200 border-amber-500/30",
  ignored: "bg-rose-500/10 text-rose-200 border-rose-500/30",
};

function RequirementMappingPanel({ requestId, requirements, locked, onUpdated }) {
  const [taskDefs, setTaskDefs] = useState([]);
  const [tdLoading, setTdLoading] = useState(false);
  const [tdError, setTdError] = useState("");
  const [busyIdx, setBusyIdx] = useState(-1);
  const [previewIdx, setPreviewIdx] = useState(-1);
  const [previewBody, setPreviewBody] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTdLoading(true);
      setTdError("");
      try {
        const r = await api.get("/task-definitions?is_active=true&limit=200");
        if (!cancelled) setTaskDefs(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (!cancelled) {
          setTdError(e.response?.data?.detail || "Failed to load task definitions");
        }
      } finally {
        if (!cancelled) setTdLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!Array.isArray(requirements) || requirements.length === 0) return null;

  const visibleTaskDefs = filter
    ? taskDefs.filter((td) =>
        (td.name || "").toLowerCase().includes(filter.toLowerCase()))
    : taskDefs;

  const patch = async (idx, body) => {
    setBusyIdx(idx);
    try {
      const r = await api.patch(
        `/admin/change-requests/${requestId}/requirements/${idx}/mapping`,
        body,
      );
      toast.success("Mapping saved");
      onUpdated?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save mapping");
    } finally {
      setBusyIdx(-1);
    }
  };

  const preview = async (idx) => {
    setPreviewIdx(idx);
    setPreviewBody(null);
    try {
      const r = await api.post(
        `/admin/change-requests/${requestId}/requirements/${idx}/preview-line-item`,
        {},
      );
      setPreviewBody(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
      setPreviewIdx(-1);
    }
  };

  return (
    <div
      className="mt-3 rounded border border-cyan-500/30 bg-cyan-500/5 p-2 space-y-2"
      data-testid="admin-requirements-mapping-panel"
    >
      <div className="flex items-center justify-between">
        <Label className="text-xs font-medium">
          Map to Task Definitions (admin-only)
        </Label>
        <span
          className="text-[10px] uppercase tracking-wide text-amber-200"
          data-testid="admin-requirements-mapping-safety-note"
        >
          Metadata only — no line items created
        </span>
      </div>
      {tdLoading && (
        <div className="text-xs text-muted-foreground italic">
          Loading task definitions…
        </div>
      )}
      {tdError && (
        <div className="text-xs text-rose-300">{tdError}</div>
      )}
      <input
        type="search"
        placeholder="Filter task definitions…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="w-full h-7 text-xs rounded border border-border bg-background px-2"
        data-testid="admin-requirements-mapping-td-filter"
      />
      <div className="space-y-2">
        {requirements.map((r, idx) => {
          const status = (r.mapping_status || "unmapped").toLowerCase();
          const busy = busyIdx === idx;
          return (
            <div
              key={idx}
              className="rounded border border-border/40 bg-background/40 p-2"
              data-testid={`admin-requirements-mapping-row-${idx}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-xs font-medium">
                  <RequirementLine value={r} />
                </div>
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded border ${MAPPING_STATUS_STYLES[status] || ""}`}
                  data-testid={`admin-requirements-mapping-status-${idx}`}
                >
                  {status.replace("_", " ")}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-1 text-xs items-end">
                <div className="sm:col-span-3">
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Status
                  </Label>
                  <select
                    value={status}
                    disabled={locked || busy}
                    onChange={(e) => patch(idx, { mapping_status: e.target.value })}
                    className="flex h-8 w-full rounded border border-input bg-background px-1 text-xs"
                    data-testid={`admin-requirements-mapping-status-select-${idx}`}
                  >
                    {MAPPING_STATUSES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-5">
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Task Definition
                  </Label>
                  <select
                    value={r.mapped_task_definition_id || ""}
                    disabled={locked || busy || tdLoading}
                    onChange={(e) => {
                      const td_id = e.target.value;
                      if (!td_id) {
                        patch(idx, { clear_mapping: true });
                      } else {
                        patch(idx, { mapped_task_definition_id: td_id });
                      }
                    }}
                    className="flex h-8 w-full rounded border border-input bg-background px-1 text-xs"
                    data-testid={`admin-requirements-mapping-td-select-${idx}`}
                  >
                    <option value="">— Select / clear —</option>
                    {visibleTaskDefs.map((td) => (
                      <option key={td.id} value={td.id}>
                        {td.name}
                        {td.is_active === false ? " (inactive)" : ""}
                      </option>
                    ))}
                    {/* Preserve the currently mapped TD even if filter
                        hides it. */}
                    {r.mapped_task_definition_id &&
                      !visibleTaskDefs.find((t) => t.id === r.mapped_task_definition_id) && (
                        <option value={r.mapped_task_definition_id}>
                          {r.mapped_task_definition_name || "(currently mapped, hidden by filter)"}
                        </option>
                      )}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Mapped qty
                  </Label>
                  <input
                    type="number"
                    min={0}
                    step="1"
                    disabled={locked || busy}
                    defaultValue={r.mapped_quantity ?? ""}
                    placeholder={String(r.quantity || 1)}
                    onBlur={(e) => {
                      const v = e.target.value;
                      if (v === "") return;
                      const n = parseFloat(v);
                      if (Number.isFinite(n)) {
                        patch(idx, { mapped_quantity: n });
                      }
                    }}
                    className="flex h-8 w-full rounded border border-input bg-background px-1 text-xs"
                    data-testid={`admin-requirements-mapping-qty-${idx}`}
                  />
                </div>
                <div className="sm:col-span-2 flex gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={locked || busy}
                    className="h-8 text-[11px] px-2 w-full"
                    onClick={() => preview(idx)}
                    data-testid={`admin-requirements-mapping-preview-${idx}`}
                  >
                    Preview
                  </Button>
                </div>
                <div className="sm:col-span-12">
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Admin mapping notes
                  </Label>
                  <input
                    type="text"
                    disabled={locked || busy}
                    defaultValue={r.admin_mapping_notes || ""}
                    placeholder="Optional admin note about this mapping"
                    maxLength={2000}
                    onBlur={(e) => {
                      const v = e.target.value;
                      if ((r.admin_mapping_notes || "") === v) return;
                      patch(idx, { admin_mapping_notes: v });
                    }}
                    className="flex h-8 w-full rounded border border-input bg-background px-1 text-xs"
                    data-testid={`admin-requirements-mapping-notes-${idx}`}
                  />
                </div>
              </div>
              {previewIdx === idx && (
                <div
                  className="mt-2 rounded border border-amber-500/40 bg-amber-500/5 p-2 text-[11px]"
                  data-testid={`admin-requirements-mapping-preview-result-${idx}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <strong>Dry-run preview (no DB writes)</strong>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => { setPreviewIdx(-1); setPreviewBody(null); }}
                    >
                      ✕
                    </button>
                  </div>
                  {!previewBody ? (
                    <span className="italic text-muted-foreground">Loading…</span>
                  ) : previewBody.would_create_line_item ? (
                    <>
                      <div className="text-amber-200 mb-1">
                        {previewBody.warning}
                      </div>
                      <pre className="overflow-x-auto bg-background/40 p-1 rounded">
                        {JSON.stringify(previewBody.preview, null, 2)}
                      </pre>
                    </>
                  ) : (
                    <span className="italic">{previewBody.reason}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Pairs of `(rawKey, displayKey)` that should be DEDUPED in favour of the
// human label. Diff table treats `venue_id` + `venue_name` as one logical
// row ("Venue") preferring the name; same for venue_location_*.
const FIELD_PAIRS = {
  venue_id: "venue_name",
  venue_location_id: "venue_location_name",
};

const FIELD_ORDER = [
  "date", "start_time", "end_time", "duration_hours", "staff_required",
  "venue_name", "venue_id", "venue_location_name", "venue_location_id",
  "activity", "name", "role", "notes",
  "project_name", "start_date", "end_date", "status",
];

function buildDiffRows(original, requested) {
  const orig = original || {};
  const req = requested || {};
  const keys = new Set([...Object.keys(orig), ...Object.keys(req)]);
  // Drop the "id" twin row if its name twin is present (e.g. drop
  // venue_id row when venue_name is in either side).
  for (const idKey of Object.keys(FIELD_PAIRS)) {
    const nameKey = FIELD_PAIRS[idKey];
    if (keys.has(nameKey)) keys.delete(idKey);
  }
  // Drop noisy housekeeping keys that admins don't need to see.
  for (const dropKey of ["shift_id", "project_id"]) keys.delete(dropKey);
  // EVENTSFLO-CLIENT-PORTAL-SHIFT-REQUIREMENTS-LINE-ITEMS-V1
  // Requirements get a dedicated structured panel — skip the flat row.
  keys.delete("requirements");

  const rows = [];
  // Push in canonical order, then any unknown keys alphabetically.
  const ordered = [
    ...FIELD_ORDER.filter((k) => keys.has(k)),
    ...[...keys].filter((k) => !FIELD_ORDER.includes(k)).sort(),
  ];
  for (const k of ordered) {
    const before = orig[k];
    const after = req[k];
    if (before === undefined && after === undefined) continue;
    // Equal? Cheap stringify covers strings/numbers/bools/null/arrays
    const equal = JSON.stringify(before ?? null) === JSON.stringify(after ?? null);
    rows.push({
      key: k,
      label: FIELD_LABELS[k] || k,
      before,
      after,
      changed: !equal && after !== undefined,
    });
  }
  return rows;
}

function renderCellValue(v) {
  if (v === null || v === undefined || v === "") {
    return <span className="text-muted-foreground italic">—</span>;
  }
  return String(v);
}

export default function ClientChangeRequestsAdminPage() {
  const [statusFilter, setStatusFilter] = useState("submitted");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // EVENTSFLO-ADMIN-CHANGE-REQUESTS-DIFF-PREVIEW-V1
  const [showUnchanged, setShowUnchanged] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: "1", page_size: "100" });
      if (statusFilter && statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      const r = await api.get(`/admin/change-requests?${params}`);
      setItems(r.data?.data || []);
    } catch (err) {
      console.error("Change requests fetch error:", err);
      toast.error(err.response?.data?.detail || "Failed to load change requests");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openDetail = (req) => {
    setDetail(req);
    setReason(req.rejection_reason || "");
    setAdminNotes(req.admin_notes || "");
    setConfirmCancel(false);
  };

  const submitReject = async () => {
    if (!detail) return;
    if (!reason.trim()) {
      toast.error("Rejection reason is required.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(
        `/admin/change-requests/${detail.id}/reject`,
        { rejection_reason: reason.trim(), admin_notes: adminNotes.trim() || null },
      );
      toast.success("Change request rejected. Client will see your reason.");
      setDetail(r.data);
      setRejectOpen(false);
      window.dispatchEvent(
        new CustomEvent("eventsflo:client-requests-pending-count-refresh"),
      );
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    } finally {
      setSubmitting(false);
    }
  };

  const submitApply = async () => {
    if (!detail) return;
    const isCancellation =
      detail.request_type === "shift_cancellation" ||
      detail.request_type === "project_cancellation";
    if (isCancellation && !confirmCancel) {
      toast.error(
        "Cancellation requests need the confirmation checkbox to apply.",
      );
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(
        `/admin/change-requests/${detail.id}/apply`,
        { confirm: isCancellation, admin_notes: adminNotes.trim() || null },
      );
      toast.success("Change applied. Live project / shift updated.");
      setDetail(r.data);
      setApplyOpen(false);
      window.dispatchEvent(
        new CustomEvent("eventsflo:client-requests-pending-count-refresh"),
      );
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply");
    } finally {
      setSubmitting(false);
    }
  };

  const isOpen = (s) => !TERMINAL.has((s || "").toLowerCase());

  return (
    <div className="space-y-4 p-6" data-testid="admin-change-requests-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Client change requests
          </h1>
          <p className="text-sm text-muted-foreground">
            Review project / shift change requests submitted from the Client
            Portal. Apply propagates the change to the live record using the
            rule-aware shift update path.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={refresh}
          data-testid="admin-change-requests-refresh"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => (
          <Button
            key={t.value}
            size="sm"
            variant={statusFilter === t.value ? "default" : "outline"}
            onClick={() => setStatusFilter(t.value)}
            data-testid={`admin-change-requests-filter-${t.value}`}
          >
            {t.label}
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Inbox className="h-4 w-4" />
            {items.length} request{items.length === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="py-10 text-center text-muted-foreground">
              <Loader2 className="h-5 w-5 mx-auto animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              No change requests in this filter.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {items.map((cr) => (
                <button
                  key={cr.id}
                  type="button"
                  onClick={() => openDetail(cr)}
                  className="w-full py-3 px-2 text-left hover:bg-accent/30 transition-colors"
                  data-testid={`admin-change-request-row-${cr.id}`}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${STATUS_STYLES[cr.status] || ""}`}
                    >
                      {cr.status}
                    </Badge>
                    <span className="font-medium text-sm">
                      {TYPE_LABELS[cr.request_type] || cr.request_type}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      · {cr.project_name || cr.project_id?.slice(0, 8)}
                    </span>
                    {cr.shift_date && (
                      <span className="text-xs text-muted-foreground">
                        · shift {cr.shift_date}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground ml-auto">
                      {cr.client_user_email || cr.client_name}
                    </span>
                  </div>
                  {cr.client_message && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                      "{cr.client_message}"
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent
          className="max-w-xl max-h-[85vh] overflow-y-auto"
          data-testid="admin-change-request-detail-dialog"
        >
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {TYPE_LABELS[detail.request_type] || detail.request_type}
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${STATUS_STYLES[detail.status] || ""}`}
                    data-testid="admin-change-request-detail-status"
                  >
                    {detail.status}
                  </Badge>
                </DialogTitle>
                <DialogDescription>
                  {detail.project_name || detail.project_id} ·{" "}
                  {detail.client_name || detail.client_user_email || "Client"}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                {detail.client_message && (
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Client message
                    </Label>
                    <p className="rounded bg-accent/30 p-2 whitespace-pre-wrap">
                      {detail.client_message}
                    </p>
                  </div>
                )}

                {/* EVENTSFLO-ADMIN-CHANGE-REQUESTS-DIFF-PREVIEW-V1 */}
                {(() => {
                  const rows = buildDiffRows(
                    detail.original_snapshot,
                    detail.requested_changes,
                  );
                  const changedCount = rows.filter((r) => r.changed).length;
                  const visibleRows = showUnchanged
                    ? rows
                    : rows.filter((r) => r.changed);
                  // EVENTSFLO-CLIENT-PORTAL-SHIFT-REQUIREMENTS-LINE-ITEMS-V1
                  const beforeReqs =
                    (detail.original_snapshot || {}).requirements;
                  const afterReqs =
                    (detail.requested_changes || {}).requirements;
                  const hasReqsDiff =
                    (Array.isArray(beforeReqs) && beforeReqs.length > 0) ||
                    (Array.isArray(afterReqs) && afterReqs.length > 0);
                  return (
                    <div data-testid="admin-change-request-diff">
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-xs text-muted-foreground">
                          Changes ({changedCount} field
                          {changedCount === 1 ? "" : "s"})
                        </Label>
                        {rows.length > changedCount && (
                          <button
                            type="button"
                            className="text-[10px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
                            onClick={() => setShowUnchanged((v) => !v)}
                            data-testid="admin-change-request-diff-toggle"
                          >
                            {showUnchanged
                              ? "Hide unchanged"
                              : `Show ${rows.length - changedCount} unchanged`}
                          </button>
                        )}
                      </div>
                      {visibleRows.length === 0 ? (
                        <div
                          className="rounded bg-accent/30 p-3 text-xs text-muted-foreground italic"
                          data-testid="admin-change-request-diff-empty"
                        >
                          {hasReqsDiff
                            ? "No basic shift fields changed — see Requested requirements below."
                            : "No fields changed."}
                        </div>
                      ) : (
                        <div className="rounded border border-border overflow-hidden">
                          <table className="w-full text-xs">
                            <thead className="bg-accent/40 text-[10px] uppercase tracking-wide text-muted-foreground">
                              <tr>
                                <th className="text-left p-2 font-medium">Field</th>
                                <th className="text-left p-2 font-medium">Current</th>
                                <th className="text-left p-2 font-medium">Requested</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                              {visibleRows.map((r) => (
                                <tr
                                  key={r.key}
                                  className={
                                    r.changed
                                      ? "bg-amber-500/5"
                                      : "opacity-60"
                                  }
                                  data-testid={`admin-change-request-diff-row-${r.key}`}
                                >
                                  <td className="p-2 font-medium align-top whitespace-nowrap">
                                    {r.label}
                                  </td>
                                  <td className="p-2 align-top text-muted-foreground">
                                    {renderCellValue(r.before)}
                                  </td>
                                  <td
                                    className={
                                      "p-2 align-top " +
                                      (r.changed ? "text-cyan-200 font-medium" : "")
                                    }
                                  >
                                    {renderCellValue(r.after)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      <details className="mt-2">
                        <summary
                          className="text-[10px] uppercase tracking-wide text-muted-foreground cursor-pointer hover:text-foreground"
                          data-testid="admin-change-request-detail-raw-summary"
                        >
                          Raw JSON (debug)
                        </summary>
                        <pre
                          className="mt-1 rounded bg-accent/30 p-2 text-[11px] overflow-x-auto"
                          data-testid="admin-change-request-detail-payload"
                        >
                          {JSON.stringify(
                            {
                              requested_changes: detail.requested_changes || {},
                              original_snapshot: detail.original_snapshot || {},
                            },
                            null,
                            2,
                          )}
                        </pre>
                      </details>
                      {/* EVENTSFLO-CLIENT-PORTAL-SHIFT-REQUIREMENTS-LINE-ITEMS-V1 */}
                      {hasReqsDiff && (
                        <div className="mt-3">
                          <RequirementsDiffPanel
                            before={beforeReqs}
                            after={afterReqs}
                          />
                          {/* EVENTSFLO-CLIENT-REQUIREMENTS-MAP-TO-TASK-DEFINITIONS-SAFE-V1 */}
                          {Array.isArray(afterReqs) && afterReqs.length > 0 && (
                            <RequirementMappingPanel
                              requestId={detail.id}
                              requirements={afterReqs}
                              locked={TERMINAL.has(detail.status)}
                              onUpdated={(fresh) => setDetail(fresh)}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {detail.rejection_reason && (
                  <div className="rounded border border-red-500/40 bg-red-500/10 p-2">
                    <Label className="text-xs text-red-300">
                      Rejection reason (client-visible)
                    </Label>
                    <p
                      className="whitespace-pre-wrap"
                      data-testid="admin-change-request-detail-rejection-reason"
                    >
                      {detail.rejection_reason}
                    </p>
                  </div>
                )}

                {detail.apply_summary && (
                  <div className="rounded border border-green-500/40 bg-green-500/10 p-2">
                    <Label className="text-xs text-green-300">Apply summary</Label>
                    <pre
                      className="whitespace-pre-wrap text-xs"
                      data-testid="admin-change-request-detail-apply-summary"
                    >
                      {detail.apply_summary}
                    </pre>
                  </div>
                )}

                {detail.admin_notes && (
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Admin notes (internal)
                    </Label>
                    <p className="rounded bg-accent/20 p-2 text-xs whitespace-pre-wrap">
                      {detail.admin_notes}
                    </p>
                  </div>
                )}
              </div>

              <DialogFooter className="gap-2 flex-wrap">
                {isOpen(detail.status) ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setRejectOpen(true)}
                      data-testid="admin-change-request-reject-open-btn"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                    <Button
                      onClick={() => setApplyOpen(true)}
                      data-testid="admin-change-request-apply-open-btn"
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      Apply
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setDetail(null)}
                    data-testid="admin-change-request-close-btn"
                  >
                    Close
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={(v) => !v && setRejectOpen(false)}>
        <DialogContent data-testid="admin-change-request-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject change request</DialogTitle>
            <DialogDescription>
              The rejection reason is shown to the client. Admin notes are
              admin-only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Rejection reason (client-visible)</Label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. We don't have enough crew available that week"
              data-testid="admin-change-request-reject-reason"
            />
            <Label>Admin notes (internal, optional)</Label>
            <Textarea
              rows={2}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              data-testid="admin-change-request-reject-notes"
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setRejectOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={submitReject}
              disabled={submitting || !reason.trim()}
              data-testid="admin-change-request-reject-submit-btn"
            >
              {submitting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply dialog */}
      <Dialog open={applyOpen} onOpenChange={(v) => !v && setApplyOpen(false)}>
        <DialogContent data-testid="admin-change-request-apply-dialog">
          <DialogHeader>
            <DialogTitle>Apply requested change</DialogTitle>
            <DialogDescription>
              {detail?.request_type === "shift_change" &&
                "This will update the live shift using the safest available service path."}
              {detail?.request_type === "project_change" &&
                "This will update safe fields on the live project (name, dates, comments)."}
              {detail?.request_type === "shift_cancellation" &&
                "This will mark the shift as cancelled. Crew assignments are not auto-removed."}
              {detail?.request_type === "project_cancellation" &&
                "This will mark the project as cancelled. Active shifts are not auto-cancelled — review individually."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(detail?.request_type === "shift_cancellation" ||
              detail?.request_type === "project_cancellation") && (
              <label
                className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer rounded border border-amber-500/40 bg-amber-500/5 p-2"
                data-testid="admin-change-request-apply-confirm-row"
              >
                <Checkbox
                  checked={confirmCancel}
                  onCheckedChange={(v) => setConfirmCancel(!!v)}
                  data-testid="admin-change-request-apply-confirm-checkbox"
                />
                <span className="flex items-start gap-1">
                  <AlertTriangle className="h-3 w-3 text-amber-300 shrink-0 mt-0.5" />
                  I confirm I want to mark this{" "}
                  {detail?.request_type === "project_cancellation"
                    ? "project"
                    : "shift"}{" "}
                  as cancelled. This action cannot be auto-reverted.
                </span>
              </label>
            )}
            <Label>Admin notes (internal, optional)</Label>
            <Textarea
              rows={2}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              data-testid="admin-change-request-apply-notes"
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setApplyOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={submitApply}
              disabled={
                submitting ||
                ((detail?.request_type === "shift_cancellation" ||
                  detail?.request_type === "project_cancellation") &&
                  !confirmCancel)
              }
              data-testid="admin-change-request-apply-submit-btn"
            >
              {submitting && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              Apply change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
